import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { API } from "../App";
import CommentSection from "../components/CommentSection";
import PhotoGallery from "../components/PhotoGallery";
import SEO from "../components/SEO";
import { format } from "date-fns";
import { ArrowLeft, Eye, Clock, User, Share2, Twitter, Facebook, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

// TikTok Embed Component with fallback
const TikTokEmbed = ({ url }) => {
  const containerRef = useRef(null);
  const [embedFailed, setEmbedFailed] = useState(false);
  
  useEffect(() => {
    // Load TikTok embed script
    const existingScript = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    } else {
      // Script already exists, try to re-render
      if (window.tiktokEmbed && window.tiktokEmbed.lib) {
        window.tiktokEmbed.lib.render();
      }
    }
    
    // Check if embed loaded after a delay
    const timeout = setTimeout(() => {
      const iframe = containerRef.current?.querySelector('iframe');
      if (!iframe) {
        setEmbedFailed(true);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [url]);
  
  // Extract video ID from URL
  const videoId = url.split('/').pop();
  
  return (
    <div ref={containerRef} className="flex justify-center">
      {!embedFailed ? (
        <blockquote 
          className="tiktok-embed" 
          cite={url}
          data-video-id={videoId}
          style={{ maxWidth: '605px', minWidth: '325px' }}
        >
          <section>
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary">Loading TikTok...</a>
          </section>
        </blockquote>
      ) : (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="inline-flex flex-col items-center gap-3 bg-card border border-border px-12 py-8 hover:border-primary/50 transition-colors group"
        >
          <svg className="h-12 w-12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
          </svg>
          <span className="font-heading font-bold text-xl group-hover:text-primary transition-colors">@donpreachaman</span>
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Watch on TikTok →</span>
        </a>
      )}
    </div>
  );
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [authorArticles, setAuthorArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${slug}`);
      setArticle(response.data);
      
      // Fetch related articles (same category)
      if (response.data.category_id) {
        const relatedRes = await axios.get(`${API}/articles?category=${response.data.category_id}&limit=3`);
        setRelatedArticles(relatedRes.data.articles.filter(a => a.slug !== slug));
      }

      // Fetch more articles by this author
      if (response.data.author_id) {
        const authorRes = await axios.get(`${API}/articles?author=${response.data.author_id}&limit=5`);
        setAuthorArticles(authorRes.data.articles.filter(a => a.slug !== slug));
      }
    } catch (error) {
      console.error("Failed to fetch article:", error);
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = window.location.href;
  
  const handleShare = (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      copy: shareUrl
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-16">
        <h1 className="font-heading font-black text-4xl uppercase tracking-tighter">Article Not Found</h1>
        <Link to="/articles" className="mt-4">
          <Button variant="outline" className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-6 font-mono uppercase tracking-widest text-xs">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Articles
          </Button>
        </Link>
      </div>
    );
  }

  const formattedDate = article.published_at 
    ? format(new Date(article.published_at), "MMMM dd, yyyy")
    : format(new Date(article.created_at), "MMMM dd, yyyy");

  // Calculate read time (roughly 200 words per minute)
  const wordCount = article.content?.split(/\s+/).length || 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div className="pt-16 min-h-screen" data-testid="article-page">
      <SEO 
        title={article.title}
        description={article.excerpt || article.content?.substring(0, 160)}
        image={article.featured_image}
        url={`/articles/${article.slug}`}
        type="article"
        keywords={article.tags || []}
        article={{
          published_at: article.published_at || article.created_at,
          authorName: article.author?.name,
          category: article.category?.name,
          tags: article.tags
        }}
      />
      {/* Hero Image */}
      <section className="relative h-[50vh] md:h-[70vh]">
        <img
          src={article.featured_image || "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=1600"}
          alt={article.title}
          className="w-full h-full object-cover object-top"
          data-testid="article-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-4 left-4 md:top-8 md:left-8">
          <Link to="/articles">
            <Button 
              variant="outline" 
              className="bg-background/80 backdrop-blur-sm border border-border text-foreground hover:bg-white hover:text-black h-10 px-4 font-mono uppercase tracking-widest text-xs"
              data-testid="article-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-[900px] mx-auto px-4 md:px-8 -mt-32 relative z-10">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {article.category && (
            <Link 
              to={`/category/${article.category.slug}`}
              className="font-mono text-xs uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 hover:bg-primary/20 transition-colors"
              data-testid="article-category"
            >
              {article.category.name}
            </Link>
          )}
          <span className="font-mono text-xs text-muted-foreground">{formattedDate}</span>
          {(article.is_archive || article.category_id === "cat_tumblr_archive") && (
            <span className="font-mono text-xs text-primary/70 border border-primary/30 px-2 py-0.5">Tumblr Archive</span>
          )}
          <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {readTime} min read
          </span>
          <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
            <Eye className="h-3 w-3" />
            {article.views?.toLocaleString() || 0} views
          </span>
        </div>

        {/* Title */}
        <h1 
          className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-none mb-8"
          data-testid="article-title"
        >
          {article.title}
        </h1>

        {/* Author & Share */}
        <div className="flex items-center justify-between pb-8 border-b border-border mb-8">
          {article.author && (
            <Link 
              to={`/authors/${article.author.author_id}`}
              className="flex items-center gap-4 group"
              data-testid="article-author"
            >
              {article.author.avatar ? (
                <img
                  src={article.author.avatar}
                  alt={article.author.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <User className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">{article.author.name}</p>
                <p className="font-mono text-xs text-muted-foreground">{article.author.role}</p>
              </div>
            </Link>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline"
                className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-4 font-mono uppercase tracking-widest text-xs"
                data-testid="article-share-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border">
              <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer">
                <Twitter className="h-4 w-4 mr-2" />
                Twitter
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleShare('facebook')} className="cursor-pointer">
                <Facebook className="h-4 w-4 mr-2" />
                Facebook
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleShare('copy')} className="cursor-pointer">
                Copy Link
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Article Body */}
        <div 
          className="prose prose-invert prose-lg max-w-none mb-12"
          data-testid="article-content"
        >
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <p className="text-foreground/90 leading-relaxed mb-6">{children}</p>
              ),
              h2: ({ children }) => (
                <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter mt-12 mb-6">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="font-heading font-bold text-xl uppercase tracking-tight mt-10 mb-4">{children}</h3>
              ),
              strong: ({ children }) => (
                <strong className="text-foreground font-bold">{children}</strong>
              ),
              img: ({ src, alt }) => (
                <figure className="my-10">
                  <img src={src} alt={alt} className="w-full border border-border" />
                  {alt && <figcaption className="font-mono text-xs text-muted-foreground mt-3 text-center uppercase tracking-wider">{alt}</figcaption>}
                </figure>
              ),
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>
              ),
              ul: ({ children }) => (
                <ul className="list-disc pl-6 mb-6 space-y-2">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-6 mb-6 space-y-2">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="text-foreground/90">{children}</li>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-foreground/70">{children}</blockquote>
              ),
            }}
          >
            {article.content}
          </ReactMarkdown>
        </div>

        {/* Video Embeds - Single or Multiple */}
        {(article.youtube_embed || article.youtube_embeds || article.tiktok_embed) && (
          <div className="mb-12 space-y-8" data-testid="article-videos">
            {/* Single YouTube embed */}
            {article.youtube_embed && !article.youtube_embeds && (
              <div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-4">Watch The Video</h3>
                <div className="aspect-video w-full relative">
                  <iframe
                    src={article.youtube_embed}
                    title="YouTube video"
                    className="w-full h-full border border-border"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <a 
                  href={article.youtube_embed.replace('/embed/', '/watch?v=')} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
                >
                  Watch on YouTube →
                </a>
              </div>
            )}
            
            {/* Multiple YouTube embeds */}
            {article.youtube_embeds && article.youtube_embeds.length > 0 && (
              <div className="space-y-8">
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight">Watch The Series</h3>
                {article.youtube_embeds.map((video, index) => (
                  <div key={index} className="border border-border p-4">
                    <h4 className="font-mono text-sm uppercase tracking-widest text-primary mb-4">{video.title}</h4>
                    <div className="aspect-video w-full">
                      <iframe
                        src={video.url}
                        title={video.title}
                        className="w-full h-full border border-border"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <a 
                      href={video.url.replace('/embed/', '/watch?v=')} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
                    >
                      Watch on YouTube →
                    </a>
                  </div>
                ))}
              </div>
            )}
            
            {/* Prose Live Performance Video */}
            {article.prose_video && (
              <div className="border border-primary/30 p-4 mt-8">
                <h4 className="font-mono text-sm uppercase tracking-widest text-primary mb-4">Prose Live at Sound Control (feat. Meany & Jun Tzu)</h4>
                <div className="aspect-video w-full">
                  <iframe
                    src={article.prose_video}
                    title="Prose Live at Sound Control"
                    className="w-full h-full border border-border"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <a 
                  href={article.prose_video.replace('/embed/', '/watch?v=')} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
                >
                  Watch on YouTube →
                </a>
              </div>
            )}
            {article.tiktok_embed && (
              <div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-4">Short Version</h3>
                <div className="flex justify-center">
                  <a 
                    href={article.tiktok_embed} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex flex-col items-center gap-3 bg-card border border-border px-12 py-8 hover:border-primary/50 transition-colors group"
                  >
                    <svg className="h-10 w-10" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                    <span className="font-heading font-bold text-xl group-hover:text-primary transition-colors">@donpreachaman</span>
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Watch on TikTok →</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reel Videos (native playback) */}
        {article.reel_videos && article.reel_videos.length > 0 && (
          <div className="mb-12" data-testid="article-reel-videos">
            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">Watch The Reels</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {article.reel_videos.map((reel, index) => (
                <div key={index} className="group" data-testid={`reel-video-${index}`}>
                  <div className="relative overflow-hidden border border-border/50 hover:border-primary/30 transition-colors duration-300">
                    <video
                      src={reel.video_url}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full bg-black"
                      style={{ maxHeight: '500px' }}
                    />
                  </div>
                  {reel.instagram_url && (
                    <a
                      href={reel.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 font-mono text-xs uppercase tracking-widest text-muted-foreground/60 hover:text-primary transition-colors"
                      data-testid={`reel-instagram-link-${index}`}
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                      Also on Instagram
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instagram Embeds (fallback link cards when no reel_videos) */}
        {(!article.reel_videos || article.reel_videos.length === 0) && article.instagram_embeds && article.instagram_embeds.length > 0 && (
          <div className="mb-12" data-testid="article-instagram">
            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">Watch The Reels</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {article.instagram_embeds.map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block border border-border hover:border-primary/50 transition-all duration-300 overflow-hidden"
                  data-testid={`instagram-reel-${index}`}
                >
                  <div className="relative bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] p-[1px]">
                    <div className="bg-card flex flex-col items-center justify-center py-12 px-6">
                      <svg className="h-10 w-10 mb-4 text-muted-foreground group-hover:text-primary transition-colors" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                      </svg>
                      <span className="font-heading font-bold uppercase tracking-tight text-sm mb-1">Reel {index + 1}</span>
                      <span className="font-mono text-xs text-muted-foreground group-hover:text-primary transition-colors">
                        Watch on Instagram →
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Photo Gallery (event/album photos) */}
        {article.photo_gallery && article.photo_gallery.length > 0 && (
          <PhotoGallery images={article.photo_gallery} title="Photo Gallery" />
        )}



        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 pb-12 border-b border-border">
            {article.tags.map((tag) => (
              <span 
                key={tag}
                className="font-mono text-xs uppercase tracking-wider text-muted-foreground bg-secondary px-3 py-1"
                data-testid={`article-tag-${tag}`}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Comments Section */}
        <div className="py-12">
          <CommentSection articleId={article.article_id} />
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 border-t border-border">
            <h3 className="font-heading font-bold text-xl uppercase tracking-tight mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedArticles.slice(0, 2).map((related) => (
                <Link 
                  key={related.article_id}
                  to={`/articles/${related.slug}`}
                  className="group flex gap-4"
                  data-testid={`related-article-${related.article_id}`}
                >
                  <img
                    src={related.featured_image || "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=200"}
                    alt={related.title}
                    className="w-24 h-24 object-cover img-grayscale"
                  />
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <span className="font-mono text-xs text-muted-foreground mt-2 block">
                      {related.published_at 
                        ? format(new Date(related.published_at), "MMM dd, yyyy")
                        : format(new Date(related.created_at), "MMM dd, yyyy")}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* More by this Author */}
        {authorArticles.length > 0 && article.author && (
          <section className="py-12 border-t border-border" data-testid="more-by-author">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
                More by {article.author.name}
              </h3>
              <Link
                to={`/authors/${article.author.author_id}`}
                className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
                data-testid="view-all-author-articles"
              >
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {authorArticles.slice(0, 3).map((item) => (
                <Link 
                  key={item.article_id}
                  to={`/articles/${item.slug}`}
                  className="group border border-border hover:border-primary/40 transition-colors"
                  data-testid={`author-article-${item.article_id}`}
                >
                  <img
                    src={item.featured_image || "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=400"}
                    alt={item.title}
                    className="w-full h-40 object-cover img-grayscale"
                  />
                  <div className="p-4">
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors line-clamp-2 text-sm">
                      {item.title}
                    </h4>
                    <p className="font-mono text-xs text-muted-foreground mt-2 line-clamp-2">
                      {item.excerpt}
                    </p>
                    <span className="font-mono text-xs text-muted-foreground/60 mt-3 block">
                      {item.published_at 
                        ? format(new Date(item.published_at), "MMM dd, yyyy")
                        : format(new Date(item.created_at), "MMM dd, yyyy")}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default ArticlePage;
